import { useState, useEffect, useRef, useCallback } from 'react';
import { productAPI, categoryAPI, IMAGE_BASE } from '../../services/api';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import MediaPickerModal from '../../components/MediaPickerModal';

export default function ProductsAdmin() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [pagination, setPagination] = useState({});
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [editProduct, setEditProduct] = useState(null);
    const [formBusy, setFormBusy] = useState(false);
    const fileInputRef = useRef();

    // Import modal state
    const [showImportModal, setShowImportModal] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importLoading, setImportLoading] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const importFileRef = useRef();
    const [importStep, setImportStep] = useState(1); // 1=dosya, 2=önizleme, 3=sonuç
    const [previewData, setPreviewData] = useState(null);
    const [productImages, setProductImages] = useState({});
    const [variantImages, setVariantImages] = useState({});
    const [expandedProducts, setExpandedProducts] = useState({});
    const [showMediaPicker, setShowMediaPicker] = useState(null);
    const [mediaSelections, setMediaSelections] = useState({});

    // Brands for autocomplete
    const [brands, setBrands] = useState([]);

    // Form state
    const [form, setForm] = useState({
        name: '', shortDescription: '', description: '', price: '', comparePrice: '',
        sku: '', stock: '0', categoryId: '', isActive: true, isFeatured: false, isDeal: false,
        trackSerialNumbers: false, hasVariants: false, brand: '',
        hasFreeShipping: false, hasFastDelivery: false, sizes: ''
    });
    const [files, setFiles] = useState([]);
    const [existingImages, setExistingImages] = useState([]);

    // Variant state (color+size based)
    const [variantSizes, setVariantSizes] = useState(''); // "XS, S, M, L, XL"
    const [colorGroups, setColorGroups] = useState([]);
    // colorGroup: { name, colorCode, sizeData: { 'XS': { enabled, stock, price }, ... }, existingImages: [] }
    const [colorFiles, setColorFiles] = useState({}); // { colorIdx: [File, ...] }
    const colorFileRefs = useRef({});

    // Legacy compat
    const [attributeTypes, setAttributeTypes] = useState([]);
    const [variants, setVariants] = useState([]);
    const [variantFiles, setVariantFiles] = useState({});
    const variantFileRefs = useRef({});

    // Media library selections for product form modal
    const [formMediaImages, setFormMediaImages] = useState([]);
    const [formVariantMediaImages, setFormVariantMediaImages] = useState({});

    // Accessory state
    const [selectedAccessories, setSelectedAccessories] = useState([]);
    const [accessorySearch, setAccessorySearch] = useState('');
    const [accessoryResults, setAccessoryResults] = useState([]);
    const [accessorySearching, setAccessorySearching] = useState(false);
    const accessoryTimerRef = useRef();

    const fetchProducts = async () => {
        setLoading(true);
        try {
            const res = await productAPI.getAll({ page, limit: 10, search });
            setProducts(res.data.products || []);
            setPagination(res.data.pagination || {});
        } catch (e) { console.error(e); }
        setLoading(false);
    };

    useEffect(() => { fetchProducts(); }, [page, search]);
    useEffect(() => {
        categoryAPI.getAll().then(res => setCategories(res.data || [])).catch(() => { });
        productAPI.getBrands().then(res => setBrands(res.data || [])).catch(() => { });
    }, []);

    const formatPrice = (p) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(p);

    const VIDEO_EXTS = ['.mp4', '.webm', '.mov', '.avi', '.mkv'];
    const isVideoUrl = (url) => VIDEO_EXTS.some(ext => (url || '').toLowerCase().split('?')[0].endsWith(ext));
    const isVideoFile = (file) => file?.type?.startsWith('video/');
    const isVideoMedia = (item) => item?.mediaType === 'video' || isVideoUrl(item?.url);

    const getImageUrl = (product) => {
        if (product.images?.length > 0) {
            const primary = product.images.find(i => i.isPrimary) || product.images[0];
            return primary.url.startsWith('http') ? primary.url : IMAGE_BASE + primary.url;
        }
        return null;
    };

    const openCreate = () => {
        setEditProduct(null);
        setForm({ name: '', shortDescription: '', description: '', price: '', comparePrice: '', sku: '', stock: '0', categoryId: '', isActive: true, isFeatured: false, isDeal: false, trackSerialNumbers: false, hasVariants: false, brand: '', hasFreeShipping: false, hasFastDelivery: false, sizes: '' });
        setFiles([]);
        setExistingImages([]);
        setVariantSizes('');
        setColorGroups([]);
        setColorFiles({});
        setAttributeTypes([]);
        setVariants([]);
        setVariantFiles({});
        setFormMediaImages([]);
        setFormVariantMediaImages({});
        setSelectedAccessories([]);
        setAccessorySearch('');
        setAccessoryResults([]);
        setShowModal(true);
    };

    const openEdit = (product) => {
        setEditProduct(product);
        setForm({
            name: product.name || '', shortDescription: product.shortDescription || '', description: product.description || '',
            price: product.price || '', comparePrice: product.comparePrice || '', sku: product.sku || '',
            stock: String(product.stock || 0), categoryId: product.categoryId || '', isActive: product.isActive, isFeatured: product.isFeatured, isDeal: product.isDeal || false,
            trackSerialNumbers: product.trackSerialNumbers || false,
            hasVariants: product.hasVariants || false,
            brand: product.brand || '',
            hasFreeShipping: product.hasFreeShipping || false,
            hasFastDelivery: product.hasFastDelivery || false,
            sizes: Array.isArray(product.sizes) ? product.sizes.join(', ') : (product.sizes || '')
        });
        setFiles([]);
        setExistingImages(product.images || []);

        // Load existing variants → convert to color groups
        if (product.hasVariants && product.variants?.length > 0) {
            const allSizes = new Set();
            const colorMap = {};
            product.variants.forEach(v => {
                const attrs = v.attributes || [];
                const colorAttr = attrs.find(a => isColorAttribute(a.name));
                const sizeAttr = attrs.find(a => !isColorAttribute(a.name));
                if (sizeAttr) allSizes.add(sizeAttr.value);
                const colorName = colorAttr?.value || 'Varsayılan';
                if (!colorMap[colorName]) {
                    colorMap[colorName] = { name: colorName, colorCode: colorAttr?.colorCode || guessColor(colorName), sizeData: {}, existingImages: [] };
                }
                if (sizeAttr) {
                    colorMap[colorName].sizeData[sizeAttr.value] = {
                        enabled: v.isActive !== false, stock: String(v.stock || 0),
                        price: v.price ? String(v.price) : '', comparePrice: v.comparePrice ? String(v.comparePrice) : '', variantId: v.id
                    };
                }
                if (v.images?.length > 0 && colorMap[colorName].existingImages.length === 0) {
                    colorMap[colorName].existingImages = v.images;
                }
            });
            setVariantSizes(Array.from(allSizes).join(', '));
            setColorGroups(Object.values(colorMap));
            // Keep legacy for submit compat
            setAttributeTypes([]);
            setVariants([]);
        } else {
            setVariantSizes('');
            setColorGroups([]);
            setAttributeTypes([]);
            setVariants([]);
        }
        setColorFiles({});
        setVariantFiles({});
        setFormMediaImages([]);
        setFormVariantMediaImages({});
        setAccessorySearch('');
        setAccessoryResults([]);
        // Load existing accessories
        productAPI.getAccessories(product.id).then(res => {
            setSelectedAccessories(res.data || []);
        }).catch(() => setSelectedAccessories([]));
        setShowModal(true);
    };

    // Accessory search
    const searchAccessories = (query) => {
        setAccessorySearch(query);
        clearTimeout(accessoryTimerRef.current);
        if (!query.trim()) { setAccessoryResults([]); return; }
        accessoryTimerRef.current = setTimeout(async () => {
            setAccessorySearching(true);
            try {
                const res = await productAPI.getAll({ search: query, limit: 20 });
                const all = res.data.products || [];
                const currentId = editProduct?.id;
                const selectedIds = selectedAccessories.map(a => a.id);
                setAccessoryResults(all.filter(p => p.id !== currentId && !selectedIds.includes(p.id)));
            } catch (e) { setAccessoryResults([]); }
            setAccessorySearching(false);
        }, 400);
    };

    // Generate variant combinations from attribute types
    const isColorAttribute = (name) => {
        const n = name.trim().toLowerCase().replace(/[ıİ]/g, 'i');
        return n === 'renk' || n === 'color' || n === 'colour';
    };

    const COLOR_NAME_MAP = {
        'siyah': '#000000', 'beyaz': '#FFFFFF', 'kırmızı': '#E53935', 'kirmizi': '#E53935',
        'mavi': '#1E88E5', 'yeşil': '#43A047', 'yesil': '#43A047', 'sarı': '#FDD835', 'sari': '#FDD835',
        'turuncu': '#FB8C00', 'mor': '#8E24AA', 'pembe': '#EC407A', 'gri': '#9E9E9E', 'gümüş': '#B0BEC5', 'gumus': '#B0BEC5',
        'kahverengi': '#6D4C41', 'lacivert': '#1A237E', 'bej': '#D7CCC8', 'bordo': '#880E4F',
        'altın': '#FFD600', 'altin': '#FFD600', 'gece yarısı': '#191970', 'gece yarisi': '#191970',
        'yıldız ışığı': '#F5F0E1', 'yildiz isigi': '#F5F0E1', 'uzay siyahı': '#1C1C1E',
        'kum': '#C2B280', 'mercan': '#FF6F61', 'deniz mavisi': '#0077B6', 'turkuaz': '#00BCD4',
        'açık mavi': '#64B5F6', 'koyu mavi': '#1565C0', 'krem': '#FFFDD0', 'haki': '#BDB76B',
        'şampanya': '#F7E7CE', 'rose gold': '#B76E79', 'gold': '#FFD600', 'silver': '#B0BEC5'
    };

    const guessColor = (name) => {
        const n = name.trim().toLowerCase();
        if (COLOR_NAME_MAP[n]) return COLOR_NAME_MAP[n];
        for (const [key, hex] of Object.entries(COLOR_NAME_MAP)) {
            if (n.includes(key) || key.includes(n)) return hex;
        }
        return '#000000';
    };

    const generateCombinations = () => {
        const parsed = attributeTypes.filter(at => at.name.trim() && at.values.trim()).map(at => ({
            name: at.name.trim(),
            values: at.values.split(',').map(v => v.trim()).filter(Boolean),
            colorCodes: at.colorCodes || {}
        }));

        if (parsed.length === 0) return;

        const combos = parsed.reduce((acc, attr) => {
            if (acc.length === 0) {
                return attr.values.map(v => {
                    const item = { name: attr.name, value: v };
                    if (isColorAttribute(attr.name)) item.colorCode = attr.colorCodes[v] || guessColor(v);
                    return [item];
                });
            }
            const result = [];
            acc.forEach(existing => {
                attr.values.forEach(v => {
                    const item = { name: attr.name, value: v };
                    if (isColorAttribute(attr.name)) item.colorCode = attr.colorCodes[v] || guessColor(v);
                    result.push([...existing, item]);
                });
            });
            return result;
        }, []);

        // Keep existing variants that match, add new ones
        const newVariants = combos.map(combo => {
            const existingVariant = variants.find(v =>
                v.attributes.length === combo.length &&
                combo.every(c => v.attributes.some(a => a.name === c.name && a.value === c.value))
            );
            if (existingVariant) return existingVariant;
            return {
                attributes: combo,
                sku: '',
                price: form.price || '',
                comparePrice: form.comparePrice || '',
                stock: '0',
                isActive: true,
                existingImages: []
            };
        });

        setVariants(newVariants);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormBusy(true);
        try {
            const formData = new FormData();
            const submitForm = { ...form };
            // Convert sizes string to JSON array
            if (submitForm.sizes && typeof submitForm.sizes === 'string') {
                const sizeArr = submitForm.sizes.split(',').map(s => s.trim()).filter(Boolean);
                submitForm.sizes = sizeArr.length > 0 ? JSON.stringify(sizeArr) : '';
            }
            Object.entries(submitForm).forEach(([key, val]) => {
                if (val !== '' && val !== null && val !== undefined) formData.append(key, val);
            });
            files.forEach(f => formData.append('images', f));

            // Add media library URLs for product
            if (formMediaImages.length > 0) {
                formData.append('mediaLibraryUrls', JSON.stringify(formMediaImages.map(m => m.url)));
            }

            // Add variants data from color groups
            if (form.hasVariants && colorGroups.length > 0) {
                const sizeList = variantSizes.split(',').map(s => s.trim()).filter(Boolean);
                const allVariants = [];
                const colorFirstVariantIdx = {};

                colorGroups.forEach((color) => {
                    let firstOfColor = true;
                    sizeList.forEach(size => {
                        const sd = color.sizeData[size];
                        if (sd?.enabled) {
                            const vIdx = allVariants.length;
                            if (firstOfColor) { colorFirstVariantIdx[color.name] = vIdx; firstOfColor = false; }
                            allVariants.push({
                                id: sd.variantId || undefined,
                                attributes: [
                                    { name: 'Renk', value: color.name, colorCode: color.colorCode },
                                    { name: 'Beden', value: size }
                                ],
                                price: sd.price || form.price,
                                comparePrice: sd.comparePrice || '',
                                stock: sd.stock || '0',
                                isActive: true
                            });
                        }
                    });
                });
                formData.append('variants', JSON.stringify(allVariants));

                // Add color images to first variant of each color
                Object.entries(colorFiles).forEach(([colorIdx, fileList]) => {
                    const colorName = colorGroups[parseInt(colorIdx)]?.name;
                    const targetIdx = colorFirstVariantIdx[colorName];
                    if (targetIdx !== undefined && fileList?.length > 0) {
                        fileList.forEach(f => formData.append(`variantImages_${targetIdx}`, f));
                    }
                });
            }

            let savedProductId = editProduct?.id;
            if (editProduct) {
                await productAPI.update(editProduct.id, formData);
            } else {
                const createRes = await productAPI.create(formData);
                savedProductId = createRes.data?.id;
            }
            // Save accessories
            if (savedProductId) {
                await productAPI.setAccessories(savedProductId, selectedAccessories.map(a => a.id));
            }
            setShowModal(false);
            fetchProducts();
            productAPI.getBrands().then(res => setBrands(res.data || [])).catch(() => { });
        } catch (err) {
            alert(err.response?.data?.error || 'Hata oluştu');
        }
        setFormBusy(false);
    };

    const handleDelete = async (id) => {
        if (!confirm('Bu ürünü silmek istediğinize emin misiniz?')) return;
        try {
            await productAPI.delete(id);
            fetchProducts();
        } catch (e) { alert('Silinemedi'); }
    };

    const handleToggle = async (id) => {
        try {
            await productAPI.toggleActive(id);
            fetchProducts();
        } catch (e) { alert('Durum değiştirilemedi'); }
    };

    const handleDeleteImage = async (imageId) => {
        try {
            await productAPI.deleteImage(imageId);
            setExistingImages(prev => prev.filter(i => i.id !== imageId));
        } catch (e) { alert('Görsel silinemedi'); }
    };

    const handleDeleteVariantImage = async (variantIdx, imageId) => {
        try {
            await productAPI.deleteVariantImage(imageId);
            setVariants(prev => prev.map((v, i) => i === variantIdx ? { ...v, existingImages: (v.existingImages || []).filter(img => img.id !== imageId) } : v));
        } catch (e) { alert('Varyant görseli silinemedi'); }
    };

    // Drag & drop reorder for images
    const dragItem = useRef(null);
    const dragOverItem = useRef(null);

    const handleDragStart = (e, index, listType) => {
        dragItem.current = { index, listType };
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', ''); // Required for Firefox
        e.currentTarget.style.opacity = '0.4';
    };

    const handleDragEnd = (e) => {
        e.currentTarget.style.opacity = '1';
        // Remove all drop highlights
        document.querySelectorAll('.image-preview.drag-over').forEach(el => el.classList.remove('drag-over'));
        dragItem.current = null;
        dragOverItem.current = null;
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        // Add visual highlight to drop target
        const target = e.currentTarget;
        if (!target.classList.contains('drag-over')) {
            document.querySelectorAll('.image-preview.drag-over').forEach(el => el.classList.remove('drag-over'));
            target.classList.add('drag-over');
        }
    };

    const handleDragLeave = (e) => {
        e.currentTarget.classList.remove('drag-over');
    };

    const handleDrop = useCallback((e, dropIndex, listType) => {
        e.preventDefault();
        if (!dragItem.current || dragItem.current.listType !== listType) return;
        const fromIndex = dragItem.current.index;
        if (fromIndex === dropIndex) return;

        if (listType === 'existing') {
            setExistingImages(prev => {
                const copy = [...prev];
                const [moved] = copy.splice(fromIndex, 1);
                copy.splice(dropIndex, 0, moved);
                // Save to backend
                productAPI.reorderImages(copy.map(img => img.id)).catch(() => {});
                return copy;
            });
        } else if (listType === 'files') {
            setFiles(prev => {
                const copy = [...prev];
                const [moved] = copy.splice(fromIndex, 1);
                copy.splice(dropIndex, 0, moved);
                return copy;
            });
        } else if (listType === 'media') {
            setFormMediaImages(prev => {
                const copy = [...prev];
                const [moved] = copy.splice(fromIndex, 1);
                copy.splice(dropIndex, 0, moved);
                return copy;
            });
        } else if (listType.startsWith('variant-existing-')) {
            const variantIdx = parseInt(listType.split('-')[2]);
            setVariants(prev => prev.map((v, i) => {
                if (i !== variantIdx) return v;
                const copy = [...(v.existingImages || [])];
                const [moved] = copy.splice(fromIndex, 1);
                copy.splice(dropIndex, 0, moved);
                productAPI.reorderVariantImages(copy.map(img => img.id)).catch(() => {});
                return { ...v, existingImages: copy };
            }));
        } else if (listType.startsWith('variant-media-')) {
            const variantIdx = parseInt(listType.split('-')[2]);
            setFormVariantMediaImages(prev => {
                const copy = [...(prev[variantIdx] || [])];
                const [moved] = copy.splice(fromIndex, 1);
                copy.splice(dropIndex, 0, moved);
                return { ...prev, [variantIdx]: copy };
            });
        }
    }, []);

    const handleFileChange = (e) => {
        setFiles(prev => [...prev, ...Array.from(e.target.files)]);
    };

    const handleVariantFileChange = (idx, e) => {
        const newFiles = Array.from(e.target.files);
        setVariantFiles(prev => ({ ...prev, [idx]: [...(prev[idx] || []), ...newFiles] }));
    };

    let searchTimer;
    const handleSearch = (val) => {
        clearTimeout(searchTimer);
        searchTimer = setTimeout(() => { setSearch(val); setPage(1); }, 400);
    };

    const downloadBlob = (blob, filename) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    };

    const handleExcelTemplate = async () => {
        try {
            const res = await productAPI.excelTemplate();
            downloadBlob(new Blob([res.data]), 'urun-sablon.xlsx');
        } catch (e) { alert('Şablon indirilemedi'); }
    };

    const handleExcelExport = async () => {
        try {
            const res = await productAPI.excelExport();
            const dateStr = new Date().toISOString().split('T')[0];
            downloadBlob(new Blob([res.data]), `urunler-export-${dateStr}.xlsx`);
        } catch (e) { alert('Export yapılamadı'); }
    };

    const handleImportPreview = async () => {
        if (!importFile) return;
        setImportLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', importFile);
            const res = await productAPI.excelPreview(formData);
            setPreviewData(res.data);
            setImportStep(2);
            // Auto-expand all products
            const expanded = {};
            res.data.products.forEach((_, i) => { expanded[i] = true; });
            setExpandedProducts(expanded);
        } catch (e) {
            setImportResult({ error: e.response?.data?.error || 'Önizleme başarısız' });
            setImportStep(3);
        }
        setImportLoading(false);
    };

    const handlePreviewFieldChange = (productIdx, field, value) => {
        setPreviewData(prev => {
            const updated = { ...prev, products: [...prev.products] };
            updated.products[productIdx] = { ...updated.products[productIdx], [field]: value };
            return updated;
        });
    };

    const handlePreviewVariantFieldChange = (productIdx, variantIdx, field, value) => {
        setPreviewData(prev => {
            const updated = { ...prev, products: [...prev.products] };
            updated.products[productIdx] = { ...updated.products[productIdx], variants: [...updated.products[productIdx].variants] };
            updated.products[productIdx].variants[variantIdx] = { ...updated.products[productIdx].variants[variantIdx], [field]: value };
            return updated;
        });
    };

    const handleProductImageSelect = (productIdx, files) => {
        setProductImages(prev => ({ ...prev, [productIdx]: Array.from(files) }));
    };

    const handleVariantImageSelect = (productIdx, variantIdx, files) => {
        setVariantImages(prev => ({ ...prev, [`${productIdx}_${variantIdx}`]: Array.from(files) }));
    };

    const handleMediaPickerSelect = (selectedItems) => {
        if (!showMediaPicker) return;
        const { context, type, productIdx, variantIdx, variantFormIdx } = showMediaPicker;

        if (context === 'form-product') {
            setFormMediaImages(prev => [...prev, ...selectedItems]);
        } else if (context === 'form-variant') {
            setFormVariantMediaImages(prev => ({
                ...prev,
                [variantFormIdx]: [...(prev[variantFormIdx] || []), ...selectedItems]
            }));
        } else {
            // import modal
            const key = type === 'product' ? `product_${productIdx}` : `variant_${productIdx}_${variantIdx}`;
            setMediaSelections(prev => ({ ...prev, [key]: [...(prev[key] || []), ...selectedItems] }));
        }
        setShowMediaPicker(null);
    };

    const removeMediaSelection = (key, idx) => {
        setMediaSelections(prev => {
            const arr = [...(prev[key] || [])];
            arr.splice(idx, 1);
            return { ...prev, [key]: arr };
        });
    };

    const handleImportSubmit = async () => {
        if (!previewData) return;
        setImportLoading(true);
        setImportResult(null);
        try {
            const formData = new FormData();

            // Enrich products with mediaLibraryUrls
            const enrichedProducts = previewData.products.map((product, pIdx) => ({
                ...product,
                mediaLibraryUrls: (mediaSelections[`product_${pIdx}`] || []).map(m => m.url),
                variants: (product.variants || []).map((variant, vIdx) => ({
                    ...variant,
                    mediaLibraryUrls: (mediaSelections[`variant_${pIdx}_${vIdx}`] || []).map(m => m.url)
                }))
            }));

            formData.append('data', JSON.stringify(enrichedProducts));

            // Append product images
            Object.entries(productImages).forEach(([idx, files]) => {
                files.forEach(file => formData.append(`productImage_${idx}`, file));
            });

            // Append variant images
            Object.entries(variantImages).forEach(([key, files]) => {
                files.forEach(file => formData.append(`variantImage_${key}`, file));
            });

            const res = await productAPI.excelImport(formData);
            setImportResult(res.data);
            setImportStep(3);
            fetchProducts();
        } catch (e) {
            setImportResult({ error: e.response?.data?.error || 'Import başarısız' });
            setImportStep(3);
        }
        setImportLoading(false);
    };

    const handleClone = async (id) => {
        try {
            const res = await productAPI.cloneProduct(id);
            fetchProducts();
            openEdit(res.data);
        } catch (e) { alert('Kopyalanamadı'); }
    };

    const handleImportDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls'))) {
            setImportFile(file);
        }
    };

    const getVariantLabel = (v) => (v.attributes || []).map(a => {
        if (a.colorCode) {
            return <span key={a.name} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: '50%', background: a.colorCode, border: '1px solid rgba(0,0,0,0.15)', flexShrink: 0 }} />
                {a.value}
            </span>;
        }
        return a.value;
    });

    const renderVariantLabel = (v) => {
        const parts = getVariantLabel(v);
        return <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{parts.map((p, i) => <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{i > 0 && ' / '}{p}</span>)}</span>;
    };

    return (
        <div>
            <div className="admin-header">
                <h1>Ürün Yönetimi</h1>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button className="btn btn-secondary" onClick={handleExcelTemplate} style={{ fontSize: '0.85rem' }}>Şablon İndir</button>
                    <button className="btn btn-secondary" onClick={handleExcelExport} style={{ fontSize: '0.85rem' }}>Excel Export</button>
                    <button className="btn btn-secondary" onClick={() => { setShowImportModal(true); setImportFile(null); setImportResult(null); setImportStep(1); setPreviewData(null); setProductImages({}); setVariantImages({}); setExpandedProducts({}); setMediaSelections({}); }} style={{ fontSize: '0.85rem' }}>Excel Import</button>
                    <button className="btn btn-primary" onClick={openCreate}>+ Yeni Ürün</button>
                </div>
            </div>

            <div className="filters-bar" style={{ marginBottom: '1rem' }}>
                <input className="search-input" placeholder="Ürün ara..." onChange={e => handleSearch(e.target.value)} />
            </div>

            {loading ? <div className="loading"><div className="spinner"></div></div> : (
                <div className="data-table-wrapper">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Ürün</th>
                                <th>Kategori</th>
                                <th>Fiyat</th>
                                <th>Stok</th>
                                <th>Durum</th>
                                <th>İşlemler</th>
                            </tr>
                        </thead>
                        <tbody>
                            {products.map(p => (
                                <tr key={p.id}>
                                    <td>
                                        <div className="product-cell">
                                            {getImageUrl(p) ? <img src={getImageUrl(p)} alt={p.name} /> : <span style={{ fontSize: '1.5rem' }}>📦</span>}
                                            <div>
                                                <div style={{ fontWeight: 600 }}>{p.name}</div>
                                                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                                                    SKU: {p.sku || '-'}
                                                    {p.hasVariants && <span style={{ marginLeft: 8, background: '#6c5ce7', color: '#fff', padding: '1px 6px', borderRadius: 4, fontSize: '0.72rem' }}>{p.variantCount || 0} varyant</span>}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>{p.category?.name || '-'}</td>
                                    <td style={{ fontWeight: 600 }}>
                                        {p.hasVariants && p.displayPrice ? (
                                            <span>{formatPrice(p.displayPrice)}'den</span>
                                        ) : formatPrice(p.price)}
                                    </td>
                                    <td>
                                        {(() => {
                                            const s = p.hasVariants ? p.totalVariantStock : p.stock;
                                            return <span style={{ fontWeight: 600, color: s === 0 ? 'var(--danger)' : s <= 5 ? '#e67e22' : 'var(--success)' }}>{s}</span>;
                                        })()}
                                    </td>
                                    <td>
                                        <label className="toggle-switch">
                                            <input type="checkbox" checked={p.isActive} onChange={() => handleToggle(p.id)} />
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            <button className="btn-icon" title="Düzenle" onClick={() => openEdit(p)}>✏️</button>
                                            <button className="btn-icon" title="Kopyala" onClick={() => handleClone(p.id)}>📋</button>
                                            <button className="btn-icon" title="Sil" onClick={() => handleDelete(p.id)} style={{ color: 'var(--danger)' }}>🗑️</button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {pagination.totalPages > 1 && (
                <div className="pagination">
                    <button disabled={page <= 1} onClick={() => setPage(page - 1)}>←</button>
                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(p => (
                        <button key={p} className={p === page ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>
                    ))}
                    <button disabled={page >= pagination.totalPages} onClick={() => setPage(page + 1)}>→</button>
                </div>
            )}

            {/* Import Modal - 3 Step */}
            {showImportModal && (
                <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && !importLoading && setShowImportModal(false)}>
                    <div className="modal" style={{ maxWidth: importStep === 2 ? 900 : 520, maxHeight: '92vh', overflow: 'auto' }}>
                        <div className="modal-header">
                            <h2>Excel Import {importStep === 1 ? '- Dosya Seç' : importStep === 2 ? '- Önizleme' : '- Sonuç'}</h2>
                            <button className="modal-close" onClick={() => !importLoading && setShowImportModal(false)}>x</button>
                        </div>
                        <div className="modal-body">
                            {/* Step indicators */}
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', marginBottom: '1.25rem' }}>
                                {[1,2,3].map(s => (
                                    <div key={s} style={{
                                        width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        fontSize: '0.85rem', fontWeight: 700,
                                        background: importStep >= s ? 'var(--primary)' : 'var(--bg-secondary)',
                                        color: importStep >= s ? '#fff' : 'var(--text-muted)',
                                        transition: 'all 0.2s'
                                    }}>{s}</div>
                                ))}
                            </div>

                            {/* STEP 1: File Selection */}
                            {importStep === 1 && (
                                <>
                                    <div
                                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                                        onDragLeave={() => setDragOver(false)}
                                        onDrop={handleImportDrop}
                                        onClick={() => importFileRef.current?.click()}
                                        style={{
                                            border: `2px dashed ${dragOver ? 'var(--primary)' : 'var(--border)'}`,
                                            borderRadius: 12, padding: '2rem', textAlign: 'center', cursor: 'pointer',
                                            background: dragOver ? 'rgba(108,92,231,0.05)' : 'transparent',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        {importFile ? (
                                            <div>
                                                <div style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 4 }}>{importFile.name}</div>
                                                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{(importFile.size / 1024).toFixed(1)} KB</div>
                                            </div>
                                        ) : (
                                            <div>
                                                <div style={{ fontSize: '2rem', marginBottom: 8 }}>+</div>
                                                <div style={{ fontWeight: 600, marginBottom: 4 }}>Excel dosyasını sürükleyin veya tıklayın</div>
                                                <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>.xlsx veya .xls dosyası</div>
                                            </div>
                                        )}
                                    </div>
                                    <input ref={importFileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
                                        onChange={(e) => e.target.files[0] && setImportFile(e.target.files[0])} />
                                    <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
                                        <button type="button" onClick={handleExcelTemplate}
                                            style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '0.85rem', textDecoration: 'underline' }}>
                                            Şablon dosyasını indir
                                        </button>
                                    </div>
                                    {importLoading && (
                                        <div style={{ marginTop: '1rem' }}>
                                            <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, height: 8, overflow: 'hidden' }}>
                                                <div style={{ background: '#6c5ce7', height: '100%', width: '100%', borderRadius: 8, animation: 'progress-indeterminate 1.5s ease-in-out infinite' }} />
                                            </div>
                                            <div style={{ textAlign: 'center', fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: 8 }}>Excel analiz ediliyor...</div>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* STEP 2: Preview */}
                            {importStep === 2 && previewData && (
                                <>
                                    {/* Summary badges */}
                                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
                                        <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 600, background: 'rgba(108,92,231,0.1)', color: 'var(--primary)' }}>
                                            {previewData.summary.totalProducts} Ürün
                                        </span>
                                        <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 600, background: 'rgba(46,204,113,0.1)', color: '#27ae60' }}>
                                            {previewData.summary.newProducts} Yeni
                                        </span>
                                        <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 600, background: 'rgba(52,152,219,0.1)', color: '#2980b9' }}>
                                            {previewData.summary.updateProducts} Güncelleme
                                        </span>
                                        <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 600, background: 'rgba(155,89,182,0.1)', color: '#8e44ad' }}>
                                            {previewData.summary.totalVariants} Varyant
                                        </span>
                                        {previewData.summary.errors > 0 && (
                                            <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 600, background: 'rgba(231,76,60,0.1)', color: '#e74c3c' }}>
                                                {previewData.summary.errors} Hata
                                            </span>
                                        )}
                                    </div>

                                    {/* Product List */}
                                    <div style={{ maxHeight: '55vh', overflow: 'auto' }}>
                                        {previewData.products.map((product, pIdx) => (
                                            <div key={pIdx} style={{
                                                border: `1px solid ${product.error ? '#e74c3c' : 'var(--border)'}`,
                                                borderRadius: 10, marginBottom: '0.5rem', overflow: 'hidden',
                                                background: product.error ? 'rgba(231,76,60,0.03)' : 'transparent'
                                            }}>
                                                {/* Product Header */}
                                                <div
                                                    onClick={() => setExpandedProducts(prev => ({ ...prev, [pIdx]: !prev[pIdx] }))}
                                                    style={{
                                                        padding: '0.6rem 0.75rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem',
                                                        background: 'var(--bg-secondary)', borderBottom: expandedProducts[pIdx] ? '1px solid var(--border)' : 'none'
                                                    }}
                                                >
                                                    <span style={{ transform: expandedProducts[pIdx] ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s', fontSize: '0.75rem' }}>&#9654;</span>
                                                    <span style={{ fontWeight: 600, flex: 1, fontSize: '0.88rem' }}>{product.name || '(isimsiz)'}</span>
                                                    {product.sku && <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{product.sku}</span>}
                                                    {/* Image count indicator */}
                                                    {(() => {
                                                        const fileCount = (productImages[pIdx] || []).length;
                                                        const mediaCount = (mediaSelections[`product_${pIdx}`] || []).length;
                                                        const total = fileCount + mediaCount;
                                                        return total > 0 ? (
                                                            <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 3 }}>
                                                                <span style={{ width: 14, height: 14, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>&#128247;</span>
                                                                {total}
                                                            </span>
                                                        ) : null;
                                                    })()}
                                                    <span style={{
                                                        padding: '2px 8px', borderRadius: 12, fontSize: '0.68rem', fontWeight: 700,
                                                        background: product.isUpdate ? 'rgba(52,152,219,0.12)' : 'rgba(46,204,113,0.12)',
                                                        color: product.isUpdate ? '#2980b9' : '#27ae60'
                                                    }}>
                                                        {product.isUpdate ? 'Güncelleme' : 'Yeni'}
                                                    </span>
                                                    {product.error && (
                                                        <span style={{ padding: '2px 8px', borderRadius: 12, fontSize: '0.68rem', fontWeight: 700, background: 'rgba(231,76,60,0.12)', color: '#e74c3c' }}>
                                                            Hata
                                                        </span>
                                                    )}
                                                </div>

                                                {/* Expanded Detail */}
                                                {expandedProducts[pIdx] && (
                                                    <div style={{ padding: '0.75rem' }}>
                                                        {product.error && (
                                                            <div style={{ padding: '0.5rem 0.75rem', background: 'rgba(231,76,60,0.08)', borderRadius: 6, color: '#e74c3c', fontSize: '0.8rem', marginBottom: '0.75rem', fontWeight: 600 }}>
                                                                {product.error}
                                                            </div>
                                                        )}

                                                        {/* Fields + Images row */}
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '1rem', alignItems: 'start' }}>
                                                            {/* Editable fields */}
                                                            <div>
                                                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.4rem' }}>
                                                                    <div>
                                                                        <label style={{ fontSize: '0.7rem', color: 'var(--text-muted)', display: 'block', marginBottom: 2 }}>Fiyat</label>
                                                                        <input type="number" step="0.01" value={product.price || ''} onChange={e => handlePreviewFieldChange(pIdx, 'price', parseFloat(e.target.value) || 0)}
                                                                            style={{ width: '100%', padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: '0.8rem' }} />
                                                                    </div>
                                                                    <div>
                                                                        <label style={{ fontSize: '0.7rem', color: 'var(--text-muted)', display: 'block', marginBottom: 2 }}>SKU</label>
                                                                        <input type="text" value={product.sku || ''} onChange={e => handlePreviewFieldChange(pIdx, 'sku', e.target.value)}
                                                                            style={{ width: '100%', padding: '5px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: '0.8rem' }} />
                                                                    </div>
                                                                </div>
                                                                <div style={{ marginTop: '0.4rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                                                                    Kategori: {product.categoryName || '-'} | Durum: {product.isActive ? 'Aktif' : 'Pasif'}
                                                                    {product.hasVariants && ' | Varyantlı'}
                                                                </div>
                                                            </div>

                                                            {/* Image area - compact */}
                                                            <div style={{ width: 140 }}>
                                                                <label style={{ fontSize: '0.7rem', color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>Görseller</label>
                                                                <div style={{ display: 'flex', gap: 4 }}>
                                                                    <div
                                                                        onClick={() => { const inp = document.createElement('input'); inp.type = 'file'; inp.accept = 'image/*'; inp.multiple = true; inp.onchange = (e) => handleProductImageSelect(pIdx, e.target.files); inp.click(); }}
                                                                        style={{
                                                                            flex: 1, border: '1.5px dashed var(--border)', borderRadius: 6, padding: '6px 4px', textAlign: 'center',
                                                                            cursor: 'pointer', fontSize: '0.7rem', color: 'var(--text-muted)', lineHeight: 1.3
                                                                        }}
                                                                    >
                                                                        {productImages[pIdx] && productImages[pIdx].length > 0
                                                                            ? <span style={{ color: 'var(--primary)', fontWeight: 600 }}>{productImages[pIdx].length} dosya</span>
                                                                            : '+ Dosya'}
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setShowMediaPicker({ type: 'product', productIdx: pIdx })}
                                                                        style={{
                                                                            flex: 1, border: '1.5px solid var(--primary)', borderRadius: 6, padding: '6px 4px',
                                                                            background: 'transparent', color: 'var(--primary)',
                                                                            fontSize: '0.68rem', cursor: 'pointer', fontWeight: 600
                                                                        }}
                                                                    >
                                                                        Havuz
                                                                    </button>
                                                                </div>
                                                                {/* Selected media thumbnails */}
                                                                {mediaSelections[`product_${pIdx}`] && mediaSelections[`product_${pIdx}`].length > 0 && (
                                                                    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', marginTop: 5 }}>
                                                                        {mediaSelections[`product_${pIdx}`].map((m, mi) => (
                                                                            <div key={mi} style={{ position: 'relative' }}>
                                                                                <img src={IMAGE_BASE + m.url} alt="" style={{ width: 30, height: 30, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--border)' }} />
                                                                                <span
                                                                                    onClick={() => removeMediaSelection(`product_${pIdx}`, mi)}
                                                                                    style={{
                                                                                        position: 'absolute', top: -4, right: -4, width: 14, height: 14,
                                                                                        background: '#e74c3c', color: '#fff', borderRadius: '50%',
                                                                                        fontSize: 9, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                                        cursor: 'pointer', lineHeight: 1, fontWeight: 700
                                                                                    }}
                                                                                >x</span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>

                                                        {/* Variants */}
                                                        {product.variants && product.variants.length > 0 && (
                                                            <div style={{ marginTop: '0.75rem', borderTop: '1px solid var(--border)', paddingTop: '0.6rem' }}>
                                                                <div style={{ fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.4rem', color: 'var(--text-muted)' }}>
                                                                    Varyantlar ({product.variants.length})
                                                                </div>
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                                                                    {product.variants.map((variant, vIdx) => {
                                                                        const vMediaKey = `variant_${pIdx}_${vIdx}`;
                                                                        const vFileKey = `${pIdx}_${vIdx}`;
                                                                        const vMediaItems = mediaSelections[vMediaKey] || [];
                                                                        const vFileItems = variantImages[vFileKey] || [];
                                                                        return (
                                                                            <div key={vIdx} style={{
                                                                                padding: '0.4rem 0.6rem',
                                                                                background: 'var(--bg-secondary)', borderRadius: 6,
                                                                                border: variant.error ? '1px solid #e74c3c' : '1px solid transparent'
                                                                            }}>
                                                                                {/* Row 1: Attributes + Fields + Badge */}
                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                                                    <div style={{ minWidth: 90, fontSize: '0.78rem', fontWeight: 600 }}>
                                                                                        {variant.attributes && variant.attributes.map((a, ai) => (
                                                                                            <span key={ai} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginRight: 5 }}>
                                                                                                {a.colorCode && <span style={{ display: 'inline-block', width: 11, height: 11, borderRadius: '50%', background: a.colorCode, border: '1px solid rgba(0,0,0,0.15)' }} />}
                                                                                                {a.value}
                                                                                            </span>
                                                                                        ))}
                                                                                        {variant.sku && <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>({variant.sku})</span>}
                                                                                    </div>
                                                                                    <input type="number" step="0.01" value={variant.price || ''} onChange={e => handlePreviewVariantFieldChange(pIdx, vIdx, 'price', parseFloat(e.target.value) || 0)}
                                                                                        title="Fiyat" placeholder="Fiyat"
                                                                                        style={{ width: 75, padding: '3px 6px', border: '1px solid var(--border)', borderRadius: 4, fontSize: '0.76rem' }} />
                                                                                    <span style={{
                                                                                        padding: '1px 6px', borderRadius: 10, fontSize: '0.63rem', fontWeight: 700,
                                                                                        background: variant.isUpdate ? 'rgba(52,152,219,0.12)' : 'rgba(46,204,113,0.12)',
                                                                                        color: variant.isUpdate ? '#2980b9' : '#27ae60'
                                                                                    }}>
                                                                                        {variant.isUpdate ? 'Gncl' : 'Yeni'}
                                                                                    </span>
                                                                                    {/* Image buttons - right aligned */}
                                                                                    <div style={{ display: 'flex', gap: 3, marginLeft: 'auto', alignItems: 'center' }}>
                                                                                        <div
                                                                                            onClick={() => { const inp = document.createElement('input'); inp.type = 'file'; inp.accept = 'image/*'; inp.multiple = true; inp.onchange = (e) => handleVariantImageSelect(pIdx, vIdx, e.target.files); inp.click(); }}
                                                                                            style={{
                                                                                                border: '1px dashed var(--border)', borderRadius: 4, padding: '2px 6px', cursor: 'pointer',
                                                                                                fontSize: '0.68rem', color: vFileItems.length > 0 ? 'var(--primary)' : 'var(--text-muted)', whiteSpace: 'nowrap',
                                                                                                fontWeight: vFileItems.length > 0 ? 600 : 400
                                                                                            }}
                                                                                        >
                                                                                            {vFileItems.length > 0 ? `${vFileItems.length} dosya` : '+ Dosya'}
                                                                                        </div>
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => setShowMediaPicker({ type: 'variant', productIdx: pIdx, variantIdx: vIdx })}
                                                                                            style={{
                                                                                                border: '1px solid var(--primary)', borderRadius: 4, padding: '2px 6px',
                                                                                                background: 'transparent', color: 'var(--primary)',
                                                                                                fontSize: '0.66rem', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap'
                                                                                            }}
                                                                                        >
                                                                                            Havuz
                                                                                        </button>
                                                                                    </div>
                                                                                </div>
                                                                                {/* Row 2: Media thumbnails + Error (only if needed) */}
                                                                                {(vMediaItems.length > 0 || variant.error) && (
                                                                                    <div style={{ marginTop: 4 }}>
                                                                                        {vMediaItems.length > 0 && (
                                                                                            <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                                                                                {vMediaItems.map((m, mi) => (
                                                                                                    <div key={mi} style={{ position: 'relative' }}>
                                                                                                        <img src={IMAGE_BASE + m.url} alt="" style={{ width: 26, height: 26, objectFit: 'cover', borderRadius: 3, border: '1px solid var(--border)' }} />
                                                                                                        <span
                                                                                                            onClick={() => removeMediaSelection(vMediaKey, mi)}
                                                                                                            style={{
                                                                                                                position: 'absolute', top: -3, right: -3, width: 12, height: 12,
                                                                                                                background: '#e74c3c', color: '#fff', borderRadius: '50%',
                                                                                                                fontSize: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                                                                cursor: 'pointer', lineHeight: 1, fontWeight: 700
                                                                                                            }}
                                                                                                        >x</span>
                                                                                                    </div>
                                                                                                ))}
                                                                                            </div>
                                                                                        )}
                                                                                        {variant.error && (
                                                                                            <div style={{ fontSize: '0.7rem', color: '#e74c3c', fontWeight: 600, marginTop: 2 }}>{variant.error}</div>
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    {importLoading && (
                                        <div style={{ marginTop: '1rem' }}>
                                            <div style={{ background: 'var(--bg-secondary)', borderRadius: 8, height: 8, overflow: 'hidden' }}>
                                                <div style={{ background: '#6c5ce7', height: '100%', width: '100%', borderRadius: 8, animation: 'progress-indeterminate 1.5s ease-in-out infinite' }} />
                                            </div>
                                            <div style={{ textAlign: 'center', fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: 8 }}>Ürünler import ediliyor...</div>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* STEP 3: Result */}
                            {importStep === 3 && importResult && (
                                <div>
                                    {importResult.error ? (
                                        <div style={{ padding: '1rem', background: 'rgba(231,76,60,0.1)', borderRadius: 8, color: 'var(--danger)', fontWeight: 600 }}>
                                            {importResult.error}
                                        </div>
                                    ) : (
                                        <>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                                                <div style={{ padding: '0.75rem', background: 'rgba(46,204,113,0.1)', borderRadius: 8, textAlign: 'center' }}>
                                                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#27ae60' }}>{importResult.productsCreated}</div>
                                                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Ürün oluşturuldu</div>
                                                </div>
                                                <div style={{ padding: '0.75rem', background: 'rgba(52,152,219,0.1)', borderRadius: 8, textAlign: 'center' }}>
                                                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#2980b9' }}>{importResult.productsUpdated}</div>
                                                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Ürün güncellendi</div>
                                                </div>
                                                <div style={{ padding: '0.75rem', background: 'rgba(46,204,113,0.1)', borderRadius: 8, textAlign: 'center' }}>
                                                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#27ae60' }}>{importResult.variantsCreated}</div>
                                                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Varyant oluşturuldu</div>
                                                </div>
                                                <div style={{ padding: '0.75rem', background: 'rgba(52,152,219,0.1)', borderRadius: 8, textAlign: 'center' }}>
                                                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#2980b9' }}>{importResult.variantsUpdated}</div>
                                                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Varyant güncellendi</div>
                                                </div>
                                            </div>
                                            {importResult.errors && importResult.errors.length > 0 && (
                                                <div style={{ marginTop: '0.5rem' }}>
                                                    <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--danger)', fontSize: '0.9rem' }}>Hatalar ({importResult.errors.length}):</div>
                                                    <div style={{ maxHeight: 150, overflow: 'auto', background: 'var(--bg-secondary)', borderRadius: 8, padding: '0.5rem', fontSize: '0.78rem' }}>
                                                        {importResult.errors.map((err, i) => (
                                                            <div key={i} style={{ padding: '3px 0', borderBottom: '1px solid var(--border)' }}>{err}</div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            {importStep === 1 && (
                                <>
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowImportModal(false)} disabled={importLoading}>İptal</button>
                                    <button type="button" className="btn btn-primary" onClick={handleImportPreview} disabled={!importFile || importLoading}>
                                        {importLoading ? 'Analiz ediliyor...' : 'Önizle'}
                                    </button>
                                </>
                            )}
                            {importStep === 2 && (
                                <>
                                    <button type="button" className="btn btn-secondary" onClick={() => { setImportStep(1); setPreviewData(null); setProductImages({}); setVariantImages({}); setMediaSelections({}); }} disabled={importLoading}>Geri</button>
                                    <button type="button" className="btn btn-primary" onClick={handleImportSubmit} disabled={importLoading}>
                                        {importLoading ? 'Import ediliyor...' : 'Import Et'}
                                    </button>
                                </>
                            )}
                            {importStep === 3 && (
                                <button type="button" className="btn btn-primary" onClick={() => setShowImportModal(false)}>Kapat</button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Product Modal */}
            {showModal && (
                <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
                    <div className="modal" style={{ maxWidth: 900, maxHeight: '92vh', overflow: 'auto' }}>
                        <div className="modal-header">
                            <h2>{editProduct ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}</h2>
                            <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Ürün Adı *</label>
                                        <input className="form-control" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                                    </div>
                                    <div className="form-group">
                                        <label>SKU</label>
                                        <input className="form-control" value={form.sku} onChange={e => setForm({ ...form, sku: e.target.value })} />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Kısa Açıklama</label>
                                    <textarea className="form-control" rows={2} value={form.shortDescription} onChange={e => setForm({ ...form, shortDescription: e.target.value })} />
                                </div>

                                <div className="form-group">
                                    <label>Detaylı Açıklama</label>
                                    <ReactQuill theme="snow" value={form.description} onChange={(val) => setForm({ ...form, description: val })} />
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Fiyat (₺) *</label>
                                        <input type="number" step="0.01" className="form-control" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} required />
                                    </div>
                                    <div className="form-group">
                                        <label>Karşılaştırma Fiyatı (₺)</label>
                                        <input type="number" step="0.01" className="form-control" value={form.comparePrice} onChange={e => setForm({ ...form, comparePrice: e.target.value })} />
                                    </div>
                                </div>

                                {!form.hasVariants && (
                                    <div className="form-group">
                                        <label>Stok Adedi</label>
                                        <input type="number" className="form-control" value={form.stock} onChange={e => setForm({ ...form, stock: e.target.value })} min="0" style={{ maxWidth: 200 }} />
                                        <small style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>Sipariş geldikçe otomatik düşer, 0 olunca satın alınamaz</small>
                                    </div>
                                )}

                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Kategori</label>
                                        <select className="form-control" value={form.categoryId} onChange={e => setForm({ ...form, categoryId: e.target.value })}>
                                            <option value="">Seçiniz</option>
                                            {categories.filter(c => !c.parentId).map(parent => {
                                                const subs = categories.filter(c => c.parentId === parent.id);
                                                return [
                                                    <option key={parent.id} value={parent.id}>{parent.name}</option>,
                                                    ...subs.map(sub => (
                                                        <option key={sub.id} value={sub.id}>&nbsp;&nbsp;&nbsp;↳ {sub.name}</option>
                                                    ))
                                                ];
                                            })}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Marka</label>
                                        <input type="text" className="form-control" placeholder="Marka adı" list="brand-list" value={form.brand} onChange={e => setForm({ ...form, brand: e.target.value })} />
                                        <datalist id="brand-list">
                                            {brands.map(b => <option key={b} value={b} />)}
                                        </datalist>
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Seçenekler</label>
                                        <div style={{ display: 'flex', gap: '1.5rem', paddingTop: '8px', flexWrap: 'wrap' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem' }}>
                                                <input type="checkbox" checked={form.isActive} onChange={e => setForm({ ...form, isActive: e.target.checked })} /> Aktif
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem' }}>
                                                <input type="checkbox" checked={form.isFeatured} onChange={e => setForm({ ...form, isFeatured: e.target.checked })} /> Öne Çıkan
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', color: '#e67e22' }}>
                                                <input type="checkbox" checked={form.isDeal} onChange={e => setForm({ ...form, isDeal: e.target.checked })} /> Haftanın Fırsatı
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem' }}>
                                                <input type="checkbox" checked={form.trackSerialNumbers} onChange={e => setForm({ ...form, trackSerialNumbers: e.target.checked })} /> Seri Numara Takibi
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600, color: 'var(--primary)' }}>
                                                <input type="checkbox" checked={form.hasVariants} onChange={e => setForm({ ...form, hasVariants: e.target.checked })} /> Varyantlı Ürün
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', color: 'var(--success)' }}>
                                                <input type="checkbox" checked={form.hasFreeShipping} onChange={e => setForm({ ...form, hasFreeShipping: e.target.checked })} /> Ücretsiz Kargo
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', color: 'var(--info)' }}>
                                                <input type="checkbox" checked={form.hasFastDelivery} onChange={e => setForm({ ...form, hasFastDelivery: e.target.checked })} /> Hızlı Teslimat
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                {/* Standalone Sizes (non-variant products) */}
                                {!form.hasVariants && (
                                    <div className="form-group" style={{ marginTop: '0.5rem' }}>
                                        <label style={{ fontWeight: 600, fontSize: '0.88rem' }}>Bedenler <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.78rem' }}>(opsiyonel)</span></label>
                                        <input className="form-control" placeholder="XS, S, M, L, XL veya STD (virgülle ayırın)" value={form.sizes}
                                            onChange={e => setForm({ ...form, sizes: e.target.value })} />
                                        <small style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>Standart beden için "STD" yazın. Ürün kartlarında görselin altında görünür.</small>
                                    </div>
                                )}

                                {/* Variant Section - Color + Size */}
                                {form.hasVariants && (
                                    <div style={{ border: '2px solid var(--primary)', borderRadius: 12, padding: '1.2rem', marginTop: '1rem', background: 'rgba(108,92,231,0.03)' }}>
                                        <h3 style={{ margin: '0 0 0.5rem', color: 'var(--primary)', fontSize: '1.05rem' }}>Renk & Beden Varyantları</h3>

                                        {/* Sizes input */}
                                        <div className="form-group" style={{ marginBottom: '1rem' }}>
                                            <label style={{ fontWeight: 600, fontSize: '0.88rem' }}>Bedenler</label>
                                            <input className="form-control" placeholder="XS, S, M, L, XL veya STD (virgülle ayırın)" value={variantSizes}
                                                onChange={e => {
                                                    setVariantSizes(e.target.value);
                                                    // Update existing colorGroups sizeData with new sizes
                                                    const newSizes = e.target.value.split(',').map(s => s.trim()).filter(Boolean);
                                                    setColorGroups(prev => prev.map(cg => {
                                                        const newSizeData = {};
                                                        newSizes.forEach(s => { newSizeData[s] = cg.sizeData[s] || { enabled: false, stock: '0', price: '', comparePrice: '' }; });
                                                        return { ...cg, sizeData: newSizeData };
                                                    }));
                                                }} />
                                        </div>

                                        {/* Color Groups */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                            {colorGroups.map((color, ci) => {
                                                const sizeList = variantSizes.split(',').map(s => s.trim()).filter(Boolean);
                                                return (
                                                    <div key={ci} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 14 }}>
                                                        {/* Color header */}
                                                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                                                            <input type="color" value={color.colorCode} onChange={e => {
                                                                const c = [...colorGroups]; c[ci] = { ...c[ci], colorCode: e.target.value }; setColorGroups(c);
                                                            }} style={{ width: 32, height: 32, padding: 0, border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer' }} />
                                                            <input className="form-control" placeholder="Renk adi (ör: Siyah)" value={color.name} onChange={e => {
                                                                const c = [...colorGroups]; c[ci] = { ...c[ci], name: e.target.value, colorCode: guessColor(e.target.value) || c[ci].colorCode }; setColorGroups(c);
                                                            }} style={{ flex: 1, fontWeight: 600 }} />
                                                            <button type="button" onClick={() => { setColorGroups(prev => prev.filter((_, i) => i !== ci)); setColorFiles(prev => { const n = { ...prev }; delete n[ci]; return n; }); }}
                                                                style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '1.1rem' }}>Kaldır</button>
                                                        </div>

                                                        {/* Color images */}
                                                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
                                                            <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginRight: 4 }}>Görseller:</span>
                                                            {(color.existingImages || []).map((img) => (
                                                                <div key={img.id} style={{ position: 'relative', width: 48, height: 48 }}>
                                                                    <img src={img.url.startsWith('http') ? img.url : IMAGE_BASE + img.url} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }} />
                                                                    <button type="button" onClick={() => {
                                                                        const c = [...colorGroups]; c[ci] = { ...c[ci], existingImages: c[ci].existingImages.filter(i => i.id !== img.id) }; setColorGroups(c);
                                                                        // Also delete from server
                                                                        productAPI.deleteVariantImage(img.id).catch(() => {});
                                                                    }} style={{ position: 'absolute', top: -4, right: -4, background: 'var(--danger)', color: '#fff', border: 'none', borderRadius: '50%', width: 16, height: 16, fontSize: '0.6rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                                                                </div>
                                                            ))}
                                                            {(colorFiles[ci] || []).map((f, fi) => (
                                                                <div key={`n-${fi}`} style={{ position: 'relative', width: 48, height: 48 }}>
                                                                    <img src={URL.createObjectURL(f)} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }} />
                                                                    <button type="button" onClick={() => setColorFiles(prev => ({ ...prev, [ci]: (prev[ci] || []).filter((_, i) => i !== fi) }))}
                                                                        style={{ position: 'absolute', top: -4, right: -4, background: 'var(--danger)', color: '#fff', border: 'none', borderRadius: '50%', width: 16, height: 16, fontSize: '0.6rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                                                                </div>
                                                            ))}
                                                            <button type="button" onClick={() => {
                                                                if (!colorFileRefs.current[ci]) {
                                                                    const input = document.createElement('input');
                                                                    input.type = 'file'; input.multiple = true; input.accept = 'image/*';
                                                                    input.onchange = (e) => {
                                                                        const newFiles = Array.from(e.target.files);
                                                                        setColorFiles(prev => ({ ...prev, [ci]: [...(prev[ci] || []), ...newFiles] }));
                                                                    };
                                                                    colorFileRefs.current[ci] = input;
                                                                }
                                                                colorFileRefs.current[ci].value = '';
                                                                colorFileRefs.current[ci].click();
                                                            }} style={{ width: 48, height: 48, border: '2px dashed var(--border)', borderRadius: 6, background: 'transparent', cursor: 'pointer', fontSize: '1.2rem', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
                                                        </div>

                                                        {/* Sizes for this color */}
                                                        {sizeList.length > 0 ? (
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                                                <div style={{ display: 'grid', gridTemplateColumns: '30px 70px 80px 90px 90px', gap: 8, fontSize: '0.72rem', fontWeight: 600, color: 'var(--text-muted)', padding: '0 4px' }}>
                                                                    <span></span><span>Beden</span><span>Stok</span><span>Fiyat (₺)</span><span>Karş. Fiyat</span>
                                                                </div>
                                                                {sizeList.map(size => {
                                                                    const sd = color.sizeData[size] || { enabled: false, stock: '0', price: '', comparePrice: '' };
                                                                    return (
                                                                        <div key={size} style={{ display: 'grid', gridTemplateColumns: '30px 70px 80px 90px 90px', gap: 8, alignItems: 'center', padding: '3px 4px', borderRadius: 6, background: sd.enabled ? 'rgba(108,92,231,0.05)' : 'transparent' }}>
                                                                            <input type="checkbox" checked={sd.enabled} onChange={e => {
                                                                                const c = [...colorGroups]; c[ci] = { ...c[ci], sizeData: { ...c[ci].sizeData, [size]: { ...sd, enabled: e.target.checked } } }; setColorGroups(c);
                                                                            }} />
                                                                            <span style={{ fontWeight: 600, fontSize: '0.88rem', opacity: sd.enabled ? 1 : 0.4 }}>{size}</span>
                                                                            <input type="number" className="form-control" value={sd.stock} min="0" disabled={!sd.enabled}
                                                                                onChange={e => { const c = [...colorGroups]; c[ci] = { ...c[ci], sizeData: { ...c[ci].sizeData, [size]: { ...sd, stock: e.target.value } } }; setColorGroups(c); }}
                                                                                style={{ padding: '4px 6px', fontSize: '0.82rem', opacity: sd.enabled ? 1 : 0.3 }} />
                                                                            <input type="number" step="0.01" className="form-control" value={sd.price} placeholder={form.price || '-'} disabled={!sd.enabled}
                                                                                onChange={e => { const c = [...colorGroups]; c[ci] = { ...c[ci], sizeData: { ...c[ci].sizeData, [size]: { ...sd, price: e.target.value } } }; setColorGroups(c); }}
                                                                                style={{ padding: '4px 6px', fontSize: '0.82rem', opacity: sd.enabled ? 1 : 0.3 }} />
                                                                            <input type="number" step="0.01" className="form-control" value={sd.comparePrice || ''} placeholder="-" disabled={!sd.enabled}
                                                                                onChange={e => { const c = [...colorGroups]; c[ci] = { ...c[ci], sizeData: { ...c[ci].sizeData, [size]: { ...sd, comparePrice: e.target.value } } }; setColorGroups(c); }}
                                                                                style={{ padding: '4px 6px', fontSize: '0.82rem', opacity: sd.enabled ? 1 : 0.3 }} />
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        ) : (
                                                            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: 0 }}>Yukarıdan bedenleri girin</p>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <button type="button" onClick={() => setColorGroups(prev => {
                                            const sizeList = variantSizes.split(',').map(s => s.trim()).filter(Boolean);
                                            const sizeData = {};
                                            sizeList.forEach(s => { sizeData[s] = { enabled: true, stock: '0', price: '', comparePrice: '' }; });
                                            return [...prev, { name: '', colorCode: '#000000', sizeData, existingImages: [] }];
                                        })} style={{ marginTop: 10, background: 'none', border: '2px dashed var(--primary)', borderRadius: 10, padding: '10px 20px', cursor: 'pointer', color: 'var(--primary)', fontWeight: 600, fontSize: '0.9rem', width: '100%' }}>
                                            + Renk Ekle
                                        </button>
                                    </div>
                                )}

                                {/* Accessories */}
                                <div className="form-group" style={{ marginTop: '1rem' }}>
                                    <label style={{ fontWeight: 600, marginBottom: 8, display: 'block' }}>Ek Ürün Önerileri (Aksesuar)</label>
                                    <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', margin: '0 0 8px' }}>
                                        Bu ürünle birlikte önerilecek aksesuarları seçin (örnek: telefona şarj aleti, kulaklık)
                                    </p>
                                    {selectedAccessories.length > 0 && (
                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                                            {selectedAccessories.map(acc => (
                                                <div key={acc.id} style={{
                                                    display: 'flex', alignItems: 'center', gap: 6,
                                                    background: 'var(--bg-secondary)', border: '1px solid var(--border)',
                                                    borderRadius: 8, padding: '5px 10px', fontSize: '0.85rem'
                                                }}>
                                                    {acc.images?.[0]?.url && (
                                                        <img
                                                            src={acc.images[0].url.startsWith('http') ? acc.images[0].url : IMAGE_BASE + acc.images[0].url}
                                                            alt="" style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'cover' }}
                                                        />
                                                    )}
                                                    <span style={{ fontWeight: 500 }}>{acc.name}</span>
                                                    <button type="button" onClick={() => setSelectedAccessories(prev => prev.filter(a => a.id !== acc.id))}
                                                        style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '1.1rem', padding: '0 2px', lineHeight: 1 }}>×</button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            className="form-control"
                                            placeholder="Ürün adı ile ara..."
                                            value={accessorySearch}
                                            onChange={e => searchAccessories(e.target.value)}
                                        />
                                        {accessoryResults.length > 0 && (
                                            <div style={{
                                                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20,
                                                background: 'var(--bg-card)', border: '1px solid var(--border)',
                                                borderRadius: '0 0 8px 8px', maxHeight: 220, overflowY: 'auto',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                            }}>
                                                {accessoryResults.map(p => (
                                                    <div key={p.id} onClick={() => {
                                                        setSelectedAccessories(prev => [...prev, p]);
                                                        setAccessorySearch('');
                                                        setAccessoryResults([]);
                                                    }} style={{
                                                        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                                                        cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: '0.88rem'
                                                    }}
                                                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
                                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                                    >
                                                        {p.images?.[0]?.url && (
                                                            <img
                                                                src={p.images[0].url.startsWith('http') ? p.images[0].url : IMAGE_BASE + p.images[0].url}
                                                                alt="" style={{ width: 32, height: 32, borderRadius: 4, objectFit: 'cover' }}
                                                            />
                                                        )}
                                                        <div>
                                                            <div style={{ fontWeight: 500 }}>{p.name}</div>
                                                            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{p.category?.name || ''}</div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {accessorySearching && <div style={{ position: 'absolute', right: 10, top: 10, fontSize: '0.8rem', color: 'var(--text-muted)' }}>Aranıyor...</div>}
                                    </div>
                                </div>

                                {/* Images */}
                                <div className="form-group" style={{ marginTop: '1rem' }}>
                                    <label>Ürün Görselleri</label>
                                    {existingImages.length > 0 && (
                                        <div className="image-previews" style={{ marginBottom: '1rem' }}>
                                            {existingImages.map((img, idx) => (
                                                <div key={img.id} className="image-preview"
                                                    draggable
                                                    onDragStart={e => handleDragStart(e, idx, 'existing')}
                                                    onDragEnd={handleDragEnd}
                                                    onDragOver={handleDragOver}
                                                    onDragLeave={handleDragLeave}
                                                    onDrop={e => { e.currentTarget.classList.remove('drag-over'); handleDrop(e, idx, 'existing'); }}
                                                    style={{ cursor: 'grab', userSelect: 'none' }}
                                                >
                                                    <div className="image-preview-order">{idx + 1}</div>
                                                    {isVideoMedia(img) ? (
                                                        <video draggable={false} src={img.url.startsWith('http') ? img.url : IMAGE_BASE + img.url} muted style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }} />
                                                    ) : (
                                                        <img draggable={false} src={img.url.startsWith('http') ? img.url : IMAGE_BASE + img.url} alt="" style={{ pointerEvents: 'none' }} />
                                                    )}
                                                    <button type="button" onClick={() => handleDeleteImage(img.id)}>×</button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: files.length > 0 || formMediaImages.length > 0 ? '0.5rem' : 0 }}>
                                        <div className="image-upload-zone" onClick={() => fileInputRef.current?.click()} style={{ flex: 1, margin: 0 }}>
                                            <p>Görsel / Video yüklemek için tıklayın</p>
                                            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>JPG, PNG, WebP, MP4, WebM — Maks 100MB</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setShowMediaPicker({ context: 'form-product' })}
                                            style={{
                                                border: '2px dashed var(--primary)', borderRadius: 8, padding: '1rem',
                                                background: 'transparent', color: 'var(--primary)', cursor: 'pointer',
                                                fontWeight: 600, fontSize: '0.85rem', minWidth: 130, textAlign: 'center',
                                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4
                                            }}
                                        >
                                            <span style={{ fontSize: '1.2rem' }}>&#128444;</span>
                                            Havuzdan Seç
                                        </button>
                                    </div>
                                    <input ref={fileInputRef} type="file" multiple accept="image/*,video/mp4,video/webm,video/quicktime" style={{ display: 'none' }} onChange={handleFileChange} />
                                    {files.length > 0 && (
                                        <div className="image-previews">
                                            {files.map((f, i) => (
                                                <div key={i} className="image-preview"
                                                    draggable
                                                    onDragStart={e => handleDragStart(e, i, 'files')}
                                                    onDragEnd={handleDragEnd}
                                                    onDragOver={handleDragOver}
                                                    onDragLeave={handleDragLeave}
                                                    onDrop={e => { e.currentTarget.classList.remove('drag-over'); handleDrop(e, i, 'files'); }}
                                                    style={{ cursor: 'grab', userSelect: 'none' }}
                                                >
                                                    <div className="image-preview-order">{existingImages.length + i + 1}</div>
                                                    {isVideoFile(f) ? (
                                                        <video draggable={false} src={URL.createObjectURL(f)} muted style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }} />
                                                    ) : (
                                                        <img draggable={false} src={URL.createObjectURL(f)} alt="" style={{ pointerEvents: 'none' }} />
                                                    )}
                                                    <button type="button" onClick={() => setFiles(files.filter((_, fi) => fi !== i))}>×</button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {formMediaImages.length > 0 && (
                                        <div className="image-previews">
                                            {formMediaImages.map((m, i) => (
                                                <div key={`media-${i}`} className="image-preview"
                                                    draggable
                                                    onDragStart={e => handleDragStart(e, i, 'media')}
                                                    onDragEnd={handleDragEnd}
                                                    onDragOver={handleDragOver}
                                                    onDragLeave={handleDragLeave}
                                                    onDrop={e => { e.currentTarget.classList.remove('drag-over'); handleDrop(e, i, 'media'); }}
                                                    style={{ cursor: 'grab', userSelect: 'none' }}
                                                >
                                                    <div className="image-preview-order">{existingImages.length + files.length + i + 1}</div>
                                                    <img draggable={false} src={IMAGE_BASE + m.url} alt={m.filename} style={{ pointerEvents: 'none' }} />
                                                    <button type="button" onClick={() => setFormMediaImages(prev => prev.filter((_, fi) => fi !== i))}>×</button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>İptal</button>
                                <button type="submit" className="btn btn-primary" disabled={formBusy}>
                                    {formBusy ? 'Kaydediliyor...' : (editProduct ? 'Güncelle' : 'Ekle')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            <MediaPickerModal
                open={!!showMediaPicker}
                onClose={() => setShowMediaPicker(null)}
                onSelect={handleMediaPickerSelect}
                multiple={true}
            />
        </div>
    );
}
